// src/components/project/ProjectDetail.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from '../../config/axiosClient';

// Icons
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import StopIcon from "@mui/icons-material/Stop";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ShareIcon from "@mui/icons-material/Share";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";

// Markdown + LaTeX
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import "katex/dist/katex.min.css";
import { sanitizeSchema, filterUntrustedIframes } from "../../config/markdownSanitize";

// Syntax highlighting
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { LIGHT_THEME, DARK_THEME } from "../../config/SyntaxThemes";

// Components
import Config from "../../config/Config";
import SEO from "../common/SEO";
import SweetLoadingSpinner from "../eyeCandy/SweetLoadingSpinner";
import ProjectContributorsList from "./ProjectContributorsList";

/* ============================================================
   Auth Gate Modal — shown when unauthenticated users try
   gated actions (like, read aloud, copy)
============================================================ */
const AuthGateModal = ({ open, onClose, action }) => {
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: "#fff", borderRadius: 12, padding: "32px 36px",
          maxWidth: 400, width: "90%", textAlign: "center",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        }}
      >
        <h3 style={{ margin: "0 0 8px", fontSize: "1.3rem", fontWeight: 700, color: "#1e293b" }}>
          Sign in to {action}
        </h3>
        <p style={{ margin: "0 0 24px", color: "#64748b", fontSize: "0.95rem", lineHeight: 1.5 }}>
          Create a free account to {action}, save your favorites, and more.
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          <Link
            to="/accounts/signin"
            style={{
              padding: "10px 24px", borderRadius: 8, fontSize: "0.95rem", fontWeight: 600,
              backgroundColor: "#59c6c1", color: "#fff", textDecoration: "none",
            }}
          >
            Sign in
          </Link>
          <Link
            to="/accounts/register"
            style={{
              padding: "10px 24px", borderRadius: 8, fontSize: "0.95rem", fontWeight: 600,
              backgroundColor: "#f1f5f9", color: "#1e293b", textDecoration: "none",
            }}
          >
            Sign up — it's free
          </Link>
        </div>
      </div>
    </div>
  );
};

/* ============================================================
   Helpers
============================================================ */
const getLanguage = (className) =>
  (className && /language-([\w-]+)/.exec(className)?.[1]?.toLowerCase()) ||
  "python";

const getCodeString = (children) => {
  if (Array.isArray(children)) return children.join("");
  return String(children ?? "");
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months !== 1 ? 's' : ''} ago`;
  const years = Math.floor(months / 12);
  return `${years} year${years !== 1 ? 's' : ''} ago`;
}

/* ============================================================
   Renderers for markdown (scoped, a11y-friendly)
   - PreBlock: fenced code with copy button
   - InlineCode: inline code styling
   - TableWrap: horizontal scroll container for wide tables
============================================================ */
const PreBlock = ({ children, isDarkMode }) => {
  const codeEl = Array.isArray(children) ? children[0] : children;
  const className = codeEl?.props?.className || "";
  const language = getLanguage(className);
  const code = getCodeString(codeEl?.props?.children).replace(/\n$/, "");

  const [copied, setCopied] = useState(false);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = code;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  }, [code]);

  return (
    <div className="codeblock-wrapper" style={{ position: "relative" }}>
    <button
      type="button"
      className={`copy-btn ${copied ? 'copied' : ''}`}
      onClick={copy}
      aria-label="Copy code"
      title={copied ? "Copied" : "Copy"}
      style={{
        position: "absolute",
        top: 6,
        right: 6,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "4px 6px",
        fontSize: 11,
        borderRadius: 4,
        border: "1px solid #e2e8f0",
        background: "rgba(255,255,255,0.9)",
        cursor: "pointer",
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        opacity: 0.15,
        transition: "opacity 150ms ease, background-color 150ms ease",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
      onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.15")}
    >
      <ContentCopyIcon style={{ fontSize: 14 }} />
      <span className="copy-text" style={{ fontSize: 10 }}>{copied ? "Copied" : "Copy"}</span>
    </button>

      <SyntaxHighlighter
        style={DARK_THEME}
        language={language}
        PreTag="div"
        customStyle={{
          paddingTop: 8,
          borderRadius: 10,
          border: "1px solid #e2e8f0",
          overflowX: "auto",
        }}
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
};

const InlineCode = ({ className, children, ...props }) => (
  <code
    className={className}
    {...props}
    style={{
      background: "var(--code-bg)",
      color: "var(--code-text)",
      borderRadius: 6,
      padding: "0.1rem 0.35rem",
      fontSize: "0.95em",
    }}
  >
    {children}
  </code>
);

/** Wrap tables in a horizontally scrollable container (matches Section 7 CSS) */
const TableWrap = ({ children, ...props }) => (
  <div className="table-scroll">
    <table {...props}>{children}</table>
  </div>
);

/* ============================================================
   Floating Edit Button (bottom-right)
============================================================ */
const FloatingEditButton = ({ to }) => (
  <Link
    to={to}
    aria-label="Edit project"
    style={{
      position: "fixed",
      bottom: 24,
      right: 24,
      zIndex: 1000,
      width: 64,
      height: 64,
      borderRadius: "50%",
      backgroundColor: "#59c6c1",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 8px 18px rgba(0,0,0,0.2)",
      textDecoration: "none",
    }}
  >
    <EditIcon style={{ color: "white", fontSize: "2.1rem" }} />
  </Link>
);

/* ============================================================
   Floating Copy Button — copies project data as JSON
============================================================ */
const FloatingCopyButton = ({ project, onAuthGate }) => {
  const [copied, setCopied] = useState(false);
  const token = useMemo(() => localStorage.getItem("TOKEN"), []);

  const handleCopy = useCallback(async () => {
    if (!token) { onAuthGate("copy this project"); return; }
    const { title, subtitle, technology, skill, body, owner } = project;
    const payload = { title, subtitle, technology, skill, body, owner };
    const json = JSON.stringify(payload, null, 2);
    try {
      await navigator.clipboard.writeText(json);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = json;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [project, token, onAuthGate]);

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label="Copy project data as JSON"
      style={{
        position: "fixed",
        bottom: 30,
        right: 156,
        zIndex: 1000,
        width: 44,
        height: 44,
        borderRadius: "50%",
        backgroundColor: copied ? "#e2e8f0" : "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        border: "1px solid var(--border)",
        cursor: "pointer",
        transition: "background-color 200ms ease",
      }}
    >
      <ContentCopyIcon style={{ color: copied ? "#59c6c1" : "#333", fontSize: "1.3rem" }} />
    </button>
  );
};

/* ============================================================
   Floating Share Button — copies project URL to clipboard
============================================================ */
const FloatingShareButton = () => {
  const [copied, setCopied] = useState(false);

  const handleShare = useCallback(async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, []);

  return (
    <button
      type="button"
      onClick={handleShare}
      aria-label="Copy link to project"
      title={copied ? "Copied!" : "Copy link"}
      style={{
        position: "fixed",
        bottom: 30,
        right: 100,
        zIndex: 1000,
        width: 44,
        height: 44,
        borderRadius: "50%",
        backgroundColor: copied ? "#e2e8f0" : "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        border: "1px solid var(--border)",
        cursor: "pointer",
        transition: "background-color 200ms ease",
      }}
    >
      <ShareIcon style={{ color: copied ? "#59c6c1" : "#333", fontSize: "1.3rem" }} />
    </button>
  );
};

/* ============================================================
   Extract speakable text SEGMENTS from a DOM element.
   Each block-level element (heading, paragraph, list item, etc.)
   becomes its own segment. Each segment is spoken as a separate
   utterance, creating natural pauses between them.
   - Inline code is read naturally within its parent element.
   - Code blocks (PRE) → each line is its own segment.
   - KaTeX annotations and script/style tags are skipped.
============================================================ */
const SKIP_TAGS = new Set(['STYLE', 'SCRIPT', 'ANNOTATION', 'SEMANTICS']);
const BLOCK_TAGS = new Set([
  'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'LI', 'BLOCKQUOTE', 'TR', 'DT', 'DD',
]);

// Make code text speakable: dots between word chars become " dot "
function speakableCode(text) {
  return text.replace(/(\w)\.(\w)/g, '$1 dot $2');
}

// Returns { segments: string[], nodes: (Element|null)[] }
// segments[i] and nodes[i] are always in sync.
function extractSegments(el, collectNodes = false) {
  const segments = [];
  const nodes = [];

  function getTextContent(node) {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent;
    if (node.nodeType !== Node.ELEMENT_NODE) return '';
    if (SKIP_TAGS.has(node.tagName)) return '';
    if (node.classList && node.classList.contains('katex-html')) return '';
    // Inline KaTeX → read the raw LaTeX from the annotation element
    if (node.classList && node.classList.contains('katex')) {
      const annotation = node.querySelector('annotation');
      return annotation ? annotation.textContent : '';
    }
    // Inline code → make dots speakable
    if (node.tagName === 'CODE') {
      return speakableCode(node.textContent);
    }
    let t = '';
    for (const child of node.childNodes) t += getTextContent(child);
    return t;
  }

  function walk(node) {
    if (node.nodeType === Node.TEXT_NODE) return;
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    if (SKIP_TAGS.has(node.tagName)) return;
    if (node.classList && node.classList.contains('katex-html')) return;

    // LaTeX block ($$...$$) → read the raw LaTeX source
    if (node.classList && node.classList.contains('katex-display')) {
      const annotation = node.querySelector('annotation');
      const raw = annotation ? annotation.textContent.trim() : '';
      if (raw) {
        segments.push(raw);
        if (collectNodes) nodes.push(node);
      }
      return;
    }

    // Code blocks — SyntaxHighlighter uses PreTag="div", wrapped in div.codeblock-wrapper
    if (node.classList && node.classList.contains('codeblock-wrapper')) {
      const lines = node.textContent.split('\n').filter(l => l.trim());
      lines.forEach((line, i) => {
        segments.push(i === 0 ? 'Code: ' + line.trim() : line.trim());
        if (collectNodes) nodes.push(node);
      });
      return;
    }
    // Fallback for bare PRE tags (e.g. in selection fragments)
    if (node.tagName === 'PRE') {
      const lines = node.textContent.split('\n').filter(l => l.trim());
      lines.forEach((line, i) => {
        const spoken = speakableCode(line.trim());
        segments.push(i === 0 ? 'Code: ' + spoken : spoken);
        if (collectNodes) nodes.push(node);
      });
      return;
    }

    if (BLOCK_TAGS.has(node.tagName)) {
      const text = getTextContent(node).replace(/\s+/g, ' ').trim();
      if (text) {
        segments.push(text);
        if (collectNodes) nodes.push(node);
      }
      return;
    }

    for (const child of node.childNodes) walk(child);
  }

  walk(el);
  return collectNodes ? { segments, nodes } : { segments };
}

/* ============================================================
   Floating Play Button — reads project content aloud
   - Each block element is spoken as its own utterance for pacing
   - Reads from text selection if present, otherwise from top
   - Reading speed from localStorage (set in gear dropdown)
============================================================ */
const READING_BORDER = '3px solid var(--brand)';
const READING_PADDING = '0 0 0 12px';

// Build a flat list of { text, node } from the rendered DOM.
// Each block-level element becomes one entry. Code blocks become one entry per line.
// node is null for header fields (title/subtitle/etc.) that aren't in the DOM.
function buildReadingList(project) {
  const items = [];

  const container = document.querySelector('.project-detail-custom');
  if (container) {
    const h1 = container.querySelector('h1');
    if (h1 && project.title) items.push({ text: project.title, node: h1 });
    // Subtitle is the div immediately after h1
    if (h1 && project.subtitle) {
      const subtitleEl = h1.nextElementSibling;
      if (subtitleEl) items.push({ text: project.subtitle, node: subtitleEl });
    }
    // Technology and skill are now separate divs; find them by their label text
    const labelSpans = container.querySelectorAll('span');
    labelSpans.forEach(span => {
      const label = span.textContent.trim();
      const parentDiv = span.parentElement;
      if (label === 'Technology:' && project.technology) {
        items.push({ text: 'Technology: ' + project.technology, node: parentDiv });
      } else if (label === 'Skill:' && project.skill) {
        items.push({ text: 'Skill: ' + project.skill, node: parentDiv });
      }
    });
    // Author(s)
    const contribNames = container.querySelectorAll('.contrib-name');
    contribNames.forEach(nameEl => {
      const name = nameEl.textContent.replace(/\s+/g, ' ').trim();
      const card = nameEl.closest('.contrib-card');
      if (name && card) items.push({ text: 'By ' + name, node: card });
    });
  }

  const bodyEl = document.querySelector('.custom-body-markdown');
  if (!bodyEl) return items;

  function getTextContent(node) {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent;
    if (node.nodeType !== Node.ELEMENT_NODE) return '';
    if (SKIP_TAGS.has(node.tagName)) return '';
    if (node.classList && node.classList.contains('katex-html')) return '';
    // Inline KaTeX → read the raw LaTeX from the annotation element
    if (node.classList && node.classList.contains('katex')) {
      const annotation = node.querySelector('annotation');
      return annotation ? annotation.textContent : '';
    }
    // Inline code → make dots speakable
    if (node.tagName === 'CODE') {
      return speakableCode(node.textContent);
    }
    let t = '';
    for (const child of node.childNodes) t += getTextContent(child);
    return t;
  }

  function walk(node) {
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    if (SKIP_TAGS.has(node.tagName)) return;
    if (node.classList && node.classList.contains('katex-html')) return;

    // LaTeX block ($$...$$) → read the raw LaTeX source
    if (node.classList && node.classList.contains('katex-display')) {
      const annotation = node.querySelector('annotation');
      const raw = annotation ? annotation.textContent.trim() : '';
      if (raw) items.push({ text: raw, node });
      return;
    }

    // Code blocks — SyntaxHighlighter uses PreTag="div", wrapped in div.codeblock-wrapper
    if (node.classList && node.classList.contains('codeblock-wrapper')) {
      // Get code text excluding the copy button
      const clone = node.cloneNode(true);
      const btn = clone.querySelector('.copy-btn');
      if (btn) btn.remove();
      const lines = clone.textContent.split('\n').filter(l => l.trim());
      lines.forEach((line, i) => {
        const spoken = speakableCode(line.trim());
        items.push({ text: i === 0 ? 'Code: ' + spoken : spoken, node });
      });
      return;
    }

    if (BLOCK_TAGS.has(node.tagName)) {
      const text = getTextContent(node).replace(/\s+/g, ' ').trim();
      if (text) items.push({ text, node });
      return;
    }

    for (const child of node.childNodes) walk(child);
  }

  walk(bodyEl);
  return items;
}

const FloatingPlayButton = ({ project, onAuthGate }) => {
  const [playState, setPlayState] = useState('stopped'); // stopped | playing | paused
  const synthRef = React.useRef(window.speechSynthesis);
  const itemsRef = React.useRef([]);     // { text, node }[]
  const idxRef = React.useRef(0);
  const prevNodeRef = React.useRef(null);
  const playStateRef = React.useRef('stopped');

  // Keep ref in sync with state so keyboard handler sees latest value
  useEffect(() => { playStateRef.current = playState; }, [playState]);

  const getSpeed = () => {
    const saved = localStorage.getItem('i7_read_speed');
    const parsed = saved ? parseFloat(saved) : null;
    return parsed && parsed >= 0.5 && parsed <= 3 ? parsed : 1.0;
  };

  const pauseTimerRef = React.useRef(null);
  const cancelledRef = React.useRef(false);

  const clearPause = () => {
    if (pauseTimerRef.current) {
      clearTimeout(pauseTimerRef.current);
      pauseTimerRef.current = null;
    }
  };

  const clearHighlight = () => {
    if (prevNodeRef.current) {
      prevNodeRef.current.style.borderLeft = '';
      prevNodeRef.current.style.padding = '';
      prevNodeRef.current.style.transition = '';
      prevNodeRef.current = null;
    }
  };

  // Speak a single item by index
  const speakAt = useCallback((idx) => {
    const synth = synthRef.current;
    const items = itemsRef.current;
    clearPause();
    if (cancelledRef.current || idx < 0 || idx >= items.length) {
      clearHighlight();
      setPlayState('stopped');
      return;
    }
    idxRef.current = idx;

    const { text, node } = items[idx];

    // Highlight
    clearHighlight();
    if (node) {
      node.style.transition = 'border-left 150ms ease, padding 150ms ease';
      node.style.borderLeft = READING_BORDER;
      node.style.padding = READING_PADDING;
      prevNodeRef.current = node;
    }

    synth.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = getSpeed();
    utterance.onend = () => {
      if (!cancelledRef.current) {
        pauseTimerRef.current = setTimeout(() => speakAt(idx + 1), 250);
      }
    };
    utterance.onerror = (e) => {
      if (e.error !== 'interrupted') setPlayState('stopped');
    };
    synth.speak(utterance);
    setPlayState('playing');
  }, []);

  // Cancel speech on mount (handles refresh mid-reading) and on unmount/project change
  useEffect(() => {
    cancelledRef.current = false;
    synthRef.current.cancel();
    return () => {
      cancelledRef.current = true;
      clearPause();
      clearHighlight();
      synthRef.current.cancel();
    };
  }, [project]);

  // Build reading list and start from the beginning
  const startPlaying = useCallback(() => {
    // Check for text selection — read from selection point onward (no highlighting)
    const selection = window.getSelection();
    if (selection && selection.toString().trim().length > 0) {
      const range = selection.getRangeAt(0);
      const bodyEl = document.querySelector('.custom-body-markdown');
      if (bodyEl && bodyEl.contains(range.startContainer)) {
        const fullRange = document.createRange();
        fullRange.setStart(range.startContainer, range.startOffset);
        fullRange.setEndAfter(bodyEl);
        const fragment = fullRange.cloneContents();
        const tempDiv = document.createElement('div');
        tempDiv.appendChild(fragment);
        const { segments } = extractSegments(tempDiv);
        if (segments.length > 0) {
          itemsRef.current = segments.map(s => ({ text: s, node: null }));
          speakAt(0);
          return;
        }
      }
    }

    const items = buildReadingList(project);
    if (items.length === 0) return;
    itemsRef.current = items;
    speakAt(0);
  }, [project, speakAt]);

  // Keyboard shortcuts: Space = play/pause/resume, ArrowDown = next, ArrowUp = prev
  useEffect(() => {
    const handleKey = (e) => {
      // Don't capture keys when typing in inputs or editable elements
      const el = document.activeElement;
      const tag = el?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (el?.isContentEditable) return;

      // Check if reading is enabled
      if (localStorage.getItem('i7_read_enabled') === 'false') return;

      const state = playStateRef.current;

      if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        const synth = synthRef.current;
        if (state === 'playing') {
          synth.pause();
          setPlayState('paused');
        } else if (state === 'paused') {
          synth.resume();
          setPlayState('playing');
        } else {
          startPlaying();
        }
      } else if (state !== 'stopped') {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          speakAt(idxRef.current + 1);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          speakAt(Math.max(0, idxRef.current - 1));
        }
      }
    };

    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [speakAt, startPlaying]);

  const tokenPlay = useMemo(() => localStorage.getItem("TOKEN"), []);

  const handleClick = useCallback(() => {
    if (!tokenPlay) { onAuthGate("listen to this project"); return; }
    const synth = synthRef.current;

    if (playState === 'playing') {
      synth.pause();
      setPlayState('paused');
      return;
    }

    if (playState === 'paused') {
      synth.resume();
      setPlayState('playing');
      return;
    }

    startPlaying();
  }, [playState, startPlaying, tokenPlay, onAuthGate]);

  const handleStop = useCallback(() => {
    clearPause();
    clearHighlight();
    synthRef.current.cancel();
    setPlayState('stopped');
  }, []);

  // Don't render if browser doesn't support speech synthesis or reading is disabled
  const readEnabled = localStorage.getItem('i7_read_enabled');
  if (!window.speechSynthesis || readEnabled === 'false') return null;

  const isActive = playState !== 'stopped';

  return (
    <div style={{ position: "fixed", bottom: 30, right: 212, zIndex: 1000, display: "flex", flexDirection: "row-reverse", gap: 8, alignItems: "center" }}>
      {isActive && (
        <button
          type="button"
          onClick={handleStop}
          aria-label="Stop reading"
          style={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            backgroundColor: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            border: "1px solid var(--border)",
            cursor: "pointer",
          }}
        >
          <StopIcon style={{ color: "#333", fontSize: "1.1rem" }} />
        </button>
      )}
      <button
        type="button"
        onClick={handleClick}
        aria-label={playState === 'playing' ? 'Pause reading' : 'Read project aloud'}
        style={{
          width: 44,
          height: 44,
          borderRadius: "50%",
          backgroundColor: isActive ? "#59c6c1" : "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          border: isActive ? "none" : "1px solid var(--border)",
          cursor: "pointer",
          transition: "background-color 200ms ease",
        }}
      >
        {playState === 'playing'
          ? <PauseIcon style={{ color: "#fff", fontSize: "1.3rem" }} />
          : <PlayArrowIcon style={{ color: isActive ? "#fff" : "#333", fontSize: "1.3rem" }} />
        }
      </button>
    </div>
  );
};

/* ============================================================
   Floating Like Button — toggles like on project
============================================================ */
const FloatingLikeButton = ({ project, onAuthGate }) => {
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(project?.like_count || 0);
  const [busy, setBusy] = useState(false);

  const token = useMemo(() => localStorage.getItem("TOKEN"), []);
  const isAuthed = !!token;

  // Check if user has liked this project
  useEffect(() => {
    if (!isAuthed || !project?.id) return;
    axios.get(`${Config().baseUrl}liked/`, {
      headers: { Authorization: `Token ${token}` },
    }).then(res => {
      if (res.data?.liked_project_ids?.includes(project.id)) {
        setLiked(true);
      }
    }).catch(() => {});
  }, [isAuthed, token, project?.id]);

  const toggle = useCallback(async () => {
    if (busy) return;
    if (!isAuthed) {
      onAuthGate("like this project");
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(
        `${Config().baseUrl}${project.id}/like/`,
        {},
        { headers: { Authorization: `Token ${token}` } }
      );
      setLiked(res.data.liked);
      setLikeCount(res.data.like_count);
    } catch (err) {
      console.error("Like toggle error:", err);
    } finally {
      setBusy(false);
    }
  }, [isAuthed, busy, token, project?.id, onAuthGate]);

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      aria-label={liked ? "Unlike project" : "Like project"}
      title={liked ? "Unlike" : "Like"}
      style={{
        position: "fixed",
        bottom: 30,
        right: 268,
        zIndex: 1000,
        width: 44,
        height: 44,
        borderRadius: "50%",
        backgroundColor: liked ? "#fef2f2" : "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        border: liked ? "1px solid #fca5a5" : "1px solid var(--border)",
        cursor: busy ? "not-allowed" : "pointer",
        transition: "all 200ms ease",
        padding: 0,
        gap: 0,
      }}
    >
      {liked
        ? <FavoriteIcon style={{ color: "#ba3f9c", fontSize: "1.2rem" }} />
        : <FavoriteBorderIcon style={{ color: "#333", fontSize: "1.2rem" }} />
      }
      {likeCount > 0 && (
        <span style={{ fontSize: 9, fontWeight: 700, color: liked ? "#ba3f9c" : "#64748b", lineHeight: 1, marginTop: -2 }}>
          {likeCount}
        </span>
      )}
    </button>
  );
};

/* ============================================================
   Reusable content renderer — used by both the detail page
   and the editor live-preview pane.
============================================================ */
export function ProjectDetailContent({
  project,
  canEdit = false,
  showActions = true,
  isDarkMode = false,
  className = "",
}) {
  const { title, subtitle, technology, skill, body, owner = [] } = project;
  const [authGateAction, setAuthGateAction] = useState(null);

  const ownerIds = useMemo(
    () => owner.map((u) => (typeof u === "object" ? u?.pk ?? u?.id : u)),
    [owner],
  );

  return (
    <div className={`project-detail-custom ${className}`.trim()} style={{ paddingBottom: showActions ? 96 : 16 }}>
      <AuthGateModal open={!!authGateAction} onClose={() => setAuthGateAction(null)} action={authGateAction} />
      {/* Banner */}
      {project.picture_link && (
        <div style={{
          width: "100%",
          height: 240,
          borderRadius: 10,
          overflow: "hidden",
          marginBottom: 16,
        }}>
          <img
            src={project.picture_link}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
          />
        </div>
      )}
      {/* Header */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ padding: "2% 0" }}>
          <h1 style={{ fontWeight: 800, marginBottom: 6 }}>{title}</h1>
          {subtitle && (
            <div style={{ fontSize: "0.9em", marginTop: 6 }}>{subtitle}</div>
          )}
          {technology && (
            <div style={{ fontSize: "0.9em", marginTop: 6 }}>
              <span style={{ color: "var(--muted)", fontSize: "0.8em", marginRight: 4 }}>Technology:</span>
              <strong style={{ color: "#59c6c1" }}>{technology}</strong>
            </div>
          )}
          {skill && (
            <div style={{ fontSize: "0.9em", marginTop: 6 }}>
              <span style={{ color: "var(--muted)", fontSize: "0.8em", marginRight: 4 }}>Skill:</span>
              <strong style={{ color: "#59c6c1" }}>{skill}</strong>
            </div>
          )}
          {showActions && (
            <div style={{ fontSize: "0.8em", marginTop: 10, display: "flex", alignItems: "center", gap: 12, color: "var(--muted)" }}>
              {project.view_count > 0 && (
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <VisibilityIcon style={{ fontSize: 16 }} />
                  {project.view_count.toLocaleString()} {project.view_count === 1 ? 'view' : 'views'}
                </span>
              )}
              {project.like_count > 0 && (
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <FavoriteIcon style={{ fontSize: 15, color: "#ba3f9c" }} />
                  {project.like_count.toLocaleString()} {project.like_count === 1 ? 'like' : 'likes'}
                </span>
              )}
              {project.created_at && (
                <span>{timeAgo(project.created_at)}</span>
              )}
              {project.updated_at && project.created_at &&
                Math.abs(new Date(project.updated_at) - new Date(project.created_at)) > 60000 && (
                <span>· updated {timeAgo(project.updated_at)}</span>
              )}
            </div>
          )}
          {ownerIds.length > 0 && (
            <div>
              <ProjectContributorsList users={ownerIds} />
            </div>
          )}
        </div>
      </div>
      <hr />

      {/* Body */}
      <div className="project-detail-body-custom">
        <div style={{ marginTop: 16 }}>
          <ReactMarkdown
            className="custom-body-markdown"
            remarkPlugins={[remarkMath, remarkGfm]}
            rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema], filterUntrustedIframes, [rehypeKatex, { strict: false }]]}
            components={{
              pre: (props) => <PreBlock {...props} isDarkMode={isDarkMode} />,
              code: InlineCode,
              table: TableWrap,
            }}
          >
            {body || ""}
          </ReactMarkdown>
        </div>
      </div>

      {/* Floating action buttons (detail page only) */}
      {showActions && (
        <>
          <FloatingLikeButton project={project} onAuthGate={setAuthGateAction} />
          <FloatingPlayButton project={project} onAuthGate={setAuthGateAction} />
          <FloatingShareButton />
          <FloatingCopyButton project={project} onAuthGate={setAuthGateAction} />
          {canEdit && <FloatingEditButton to={`/projects/${project.id || project.pk}/edit`} />}
        </>
      )}
    </div>
  );
}

/* ============================================================
   Page-level component (fetches project by ID from route params)
============================================================ */
export default function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);

  // Detect dark mode from localStorage (same as NavSettingsDropdown)
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('i7-dark-mode');
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Listen for theme changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.classList.contains('dark-theme'));
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  const userPk = useMemo(() => {
    const raw = localStorage.getItem("USER_PK");
    const parsed = raw ? parseInt(raw, 10) : null;
    return Number.isFinite(parsed) ? parsed : null;
  }, []);

  const baseUrl = useMemo(() => Config().baseUrl, []);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    (async () => {
      try {
        setLoading(true);
        setLoadErr(null);

        const res = await axios.get(`${baseUrl}${id}`, {
          signal: controller.signal,
        });
        if (cancelled) return;

        const data = res.data || {};
        setProject(data);

        const ownerPks = Array.isArray(data.owner)
          ? data.owner.map((u) => u?.pk ?? u?.id ?? u)
          : [];

        setCanEdit(userPk !== null && ownerPks.includes(userPk));
      } catch (e) {
        console.error('ProjectDetail Load Error:', e);
        console.error('ProjectDetail Load Error Response:', e.response);
        console.error('ProjectDetail Load Error Status:', e.response?.status);
        console.error('ProjectDetail Load Error Data:', e.response?.data);

        let errorMessage = "Failed to load project";
        if (e.response?.status === 404) {
          errorMessage = "Project not found";
        } else if (e.response?.status === 403) {
          errorMessage = "You don't have permission to view this project";
        } else if (e.response?.status === 500) {
          errorMessage = "Server error occurred while loading project";
        }

        if (!cancelled) setLoadErr(errorMessage);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    window.scrollTo(0, 0);

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [id, userPk, baseUrl]);

  if (loading) return <SweetLoadingSpinner loadingState={true} />;

  if (loadErr || !project) {
    return (
      <div className="container" style={{ padding: "2rem 1rem" }}>
        <h2 style={{ marginBottom: 8 }}>Could not load project</h2>
        <p style={{ color: "#991b1b" }}>{String(loadErr || "Unknown error")}</p>
        <p>
          <Link to="/projects" className="pretty-button">
            Back to Projects
          </Link>
        </p>
      </div>
    );
  }

  const ownerNames = Array.isArray(project.owner)
    ? project.owner.map(o => o?.username || '').filter(Boolean).join(', ')
    : '';
  const projectDesc = project.subtitle
    || [project.technology, project.skill].filter(Boolean).join(' — ')
    || 'A project on Inertia7.';

  return (
    <div className="container">
      <SEO
        title={project.title}
        description={`${projectDesc}${ownerNames ? ` by ${ownerNames}` : ''}`}
        image={project.picture_link || undefined}
        url={`/projects/${id}`}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": project.title,
          "description": projectDesc,
          "url": `https://www.inertia7.com/projects/${id}`,
          ...(project.picture_link ? { "image": project.picture_link } : {}),
          ...(ownerNames ? { "author": { "@type": "Person", "name": ownerNames } } : {}),
          "publisher": { "@type": "Organization", "name": "Inertia7" },
        }}
      />
      <ProjectDetailContent
        project={project}
        canEdit={canEdit}
        showActions
        isDarkMode={isDarkMode}
      />
    </div>
  );
}
